import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  clearAccessToken,
  setAccessToken,
  broadcastLogout,
  createAuthThunks,
} from '@adthlete/shared-frontend'
import athleteApi from '../lib/api'
import {
  updateProfile,
  updateSports,
  updatePreferences,
  updateSchool,
  updateAvatarUrl,
  UpdateProfileRequest,
  UpdateSportsRequest,
  UpdatePreferencesRequest,
  UpdateSchoolRequest,
} from '../lib/athleteApi'

const authThunks = createAuthThunks({ api: athleteApi, userType: 'athlete', typePrefix: 'user' })
export const {
  login: loginThunk,
  fetchCurrentUser,
  logout: logoutThunk,
  requestMagicLink: requestMagicLinkThunk,
  verifyMagicLink: verifyMagicLinkThunk,
  confirmEmail: confirmEmailThunk,
} = authThunks

export const signupThunk = createAsyncThunk(
  'user/signup',
  async (
    userData: {
      name: string
      email: string
      password: string
      phone?: string
      school_id: number | null
      sports?: number[]
      socialHandles?: { platform: string; handle: string }[]
      date_of_birth: string
      agreed_to_terms: boolean
      legal_version: string
    },
    { rejectWithValue },
  ) => {
    const {
      name,
      email,
      password,
      phone,
      school_id,
      sports,
      socialHandles,
      date_of_birth,
      agreed_to_terms,
      legal_version,
    } = userData
    try {
      // Registration only, no automatic login
      const response = await athleteApi.post('/users/register', {
        name,
        email,
        password,
        phone,
        school_id,
        sports,
        socialHandles,
        user_type: 'athlete',
        date_of_birth,
        agreed_to_terms,
        legal_version,
      })
      // Return the response which includes the user's email
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Registration failed')
    }
  },
)

// Thunk to resend confirmation email
export const resendConfirmationThunk = createAsyncThunk(
  'user/resendConfirmation',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await athleteApi.post('/users/resend-confirmation', {
        email,
        user_type: 'athlete',
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resend confirmation email')
    }
  },
)

// Thunk to close (deactivate) the account
export const closeAccountThunk = createAsyncThunk(
  'user/closeAccount',
  async (reason: string | undefined, { rejectWithValue }) => {
    try {
      const response = await athleteApi.post('/users/me/close', { reason })
      clearAccessToken()
      broadcastLogout()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to close account')
    }
  },
)

// Thunk to disconnect a social account: revokes the OAuth grant with the
// provider and removes our local tokens/link, not just client-side state.
export const disconnectSocialMediaThunk = createAsyncThunk(
  'user/disconnectSocialMedia',
  async (platform: string, { rejectWithValue }) => {
    try {
      await athleteApi.delete(`/athlete/oauth/${platform}`)
      return { platform }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disconnect account')
    }
  },
)

// Thunk to update athlete profile
export const updateProfileThunk = createAsyncThunk(
  'user/updateProfile',
  async (profileData: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const response = await updateProfile(profileData)
      return response.user // expecting { user: { id, name, email } }
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

// Thunk to update athlete sports
export const updateSportsThunk = createAsyncThunk(
  'user/updateSports',
  async (sportsData: UpdateSportsRequest, { rejectWithValue }) => {
    try {
      const response = await updateSports(sportsData)
      return response.sports // expecting { sports: string[] }
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

// Thunk to update athlete preferences
export const updatePreferencesThunk = createAsyncThunk(
  'user/updatePreferences',
  async (preferencesData: UpdatePreferencesRequest, { rejectWithValue }) => {
    try {
      const response = await updatePreferences(preferencesData)
      return response.preferences // expecting full preferences object
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

// Thunk to update athlete school
export const updateSchoolThunk = createAsyncThunk(
  'user/updateSchool',
  async (schoolData: UpdateSchoolRequest, { rejectWithValue }) => {
    try {
      const response = await updateSchool(schoolData)
      return response.school // { id, name } | null
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

// Thunk to update athlete avatar
export const updateAvatarThunk = createAsyncThunk(
  'user/updateAvatar',
  async (avatarData: { avatarUrl: string }, { rejectWithValue }) => {
    try {
      // Backend already updated the avatar in database during upload
      // Just return the URL to update Redux state
      return avatarData.avatarUrl
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

// Thunk to send phone verification code
export const sendPhoneCodeThunk = createAsyncThunk(
  'user/sendPhoneCode',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await athleteApi.post('/users/phone/send-code', { phone })
      return response.data // expecting { message: 'Verification code sent' }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send verification code')
    }
  },
)

// Thunk to verify phone code
export const verifyPhoneCodeThunk = createAsyncThunk(
  'user/verifyPhoneCode',
  async ({ phone, code }: { phone: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await athleteApi.post('/users/phone/verify', { phone, code })
      // The backend re-issues the access token with the phone_verified claim, which lifts the
      // phone gate (requirePhoneVerified) without waiting for the next refresh
      if (response.data.token) setAccessToken(response.data.token)
      return response.data // expecting { message: 'Phone verified successfully', token }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Verification failed')
    }
  },
)

// Thunk to remove phone number
export const removePhoneThunk = createAsyncThunk(
  'user/removePhone',
  async (_, { rejectWithValue }) => {
    try {
      const response = await athleteApi.delete('/users/phone')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove phone number')
    }
  },
)

export interface SocialAccount {
  id: string
  platform: 'instagram' | 'tiktok' | 'youtube' | 'facebook'
  platform_account_name: string | null
  platform_account_id: string
  platform_account_display_name: string
  picture_main_url: string
  oauth_connected?: boolean
  /** The connection expired or was revoked; stats stop updating until the athlete reconnects */
  needs_reconnect?: boolean
}

export interface AuthTokenInfo {
  id: string
  platform: 'facebook' | 'instagram' | 'youtube' | 'tiktok'
}

export interface PaymentMethod {
  id: number
  method_type: string
}

interface UserState {
  currentUser: {
    id: string
    email: string
    phone: string | null
    phone_verified: boolean
    // Production only: the app must show the phone gate before anything else
    phone_verification_required?: boolean
    user_type: string
    role: string
    name: string
    avatar: string
    gender: string
    school?: {
      id: string
      name: string
      /** The Creator Lab works on this campus */
      creatorLab?: boolean
    } | null
    preferences: {
      interests: string[]
      contentStyles: string[]
      preferredBrands: string[]
      excludedCategories: string[]
    }
    is_active: boolean
    email_verified: boolean
    searchable: boolean
    created_at: string
    updated_at: string
    school_id: number | null
    org_id: number | null
    sports: number[]
    paymentMethods: PaymentMethod[]
    socialAccounts: SocialAccount[]
    authTokenInfo: AuthTokenInfo[]
    pendingSocialHandles?: { platform: string; handle: string }[]
    welcome_completed?: boolean
    bio?: string | null
    position?: string | null
    public_profile_username?: string | null
    /** First opened from the phone's home screen (installed as an app) */
    app_installed_at?: string | null
    /** At least one device has push notifications turned on */
    push_enabled?: boolean
    /** Separate days the athlete has come back during onboarding (Day N of 7) */
    onboarding_active_days?: number
    public_profile_published?: boolean
    /** false for an account created from the website that hasn't set a password yet */
    has_password?: boolean
    password_setup_required?: boolean
    terms_required?: boolean
    /** Joined without a school (from the website); asked for with the terms */
    school_required?: boolean
    rate_card?: unknown
    content_themes?: { id: string; label: string }[] | null
    academy?: {
      watched: number
      lessons?: string[]
      actions?: Record<string, import('@adthlete/common/utils/academy').LessonAction>
    } | null
    record?: { acceptedDeals: number; completedDeals: number; overdueDeals: number } | null
    level_earned?: 'L1' | 'L2' | 'L3' | 'L4' | null
    posting?: {
      goal: number
      thisWeek: number
      weekStart: string
      weeks: { weekStart: string; posted: number }[]
    } | null
    mailing_address?: {
      line1: string
      line2?: string | null
      city: string
      state: string
      postal_code: string
      country?: string
    } | null
  } | null
  loading: boolean
  profileLoading: boolean
  sportsLoading: boolean
  error: string | null
}

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null, // Store user info here, e.g., { id, email }
    loading: true, // start in loading state until current user is fetched
    profileLoading: false,
    sportsLoading: false,
    error: null,
  } as UserState, // Assert the type of initialState
  reducers: {
    // Update user fields with partial data
    updateUser: (state, action: PayloadAction<Partial<UserState['currentUser']>>) => {
      if (!state.currentUser) return
      state.currentUser = { ...state.currentUser, ...action.payload }
    },
    // Clear auth state when the session ends (refresh failed, or another tab logged out).
    clearUser: (state) => {
      state.currentUser = null
      state.loading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginThunk.fulfilled, (state, action: PayloadAction<UserState['currentUser']>) => {
        state.loading = false
        state.currentUser = action.payload // Save full user data to state
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(signupThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signupThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false
        // Don't set current user on signup, wait for email verification
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      // Handle page refresh / initial load
      .addCase(fetchCurrentUser.pending, (state) => {
        // Only the first load (no user yet) counts as loading. Re-fetching after a save
        // happens in the background, so the screen doesn't blank out and come back.
        if (!state.currentUser) state.loading = true
        state.error = null
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (state, action: PayloadAction<UserState['currentUser']>) => {
          state.loading = false
          state.currentUser = action.payload
        },
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.loading = false
        state.currentUser = null // Clear user data on logout
      })
      .addCase(closeAccountThunk.fulfilled, (state) => {
        state.loading = false
        state.currentUser = null // Clear user data on account closure
      })
      .addCase(disconnectSocialMediaThunk.fulfilled, (state, action) => {
        if (state.currentUser) {
          state.currentUser.socialAccounts = state.currentUser.socialAccounts.filter(
            (acc) => acc.platform !== action.payload.platform,
          )
        }
      })
      .addCase(confirmEmailThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(confirmEmailThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false
        state.currentUser = action.payload // Set user data after email confirmation (payload is now the user object directly)
      })
      .addCase(confirmEmailThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(updateProfileThunk.pending, (state) => {
        state.profileLoading = true
        state.error = null
      })
      .addCase(
        updateProfileThunk.fulfilled,
        (
          state,
          action: PayloadAction<{
            id: string
            name?: string
            email?: string
            searchable?: boolean
          }>,
        ) => {
          state.profileLoading = false
          if (state.currentUser && action.payload) {
            if (action.payload.name !== undefined) {
              state.currentUser.name = action.payload.name
            }
            if (action.payload.email !== undefined) {
              state.currentUser.email = action.payload.email
            }
            if (action.payload.searchable !== undefined) {
              state.currentUser.searchable = action.payload.searchable
            }
          }
        },
      )
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.profileLoading = false
        state.error = action.payload as string
      })
      .addCase(updateSportsThunk.pending, (state) => {
        state.sportsLoading = true
        state.error = null
      })
      .addCase(updateSportsThunk.fulfilled, (state, action: PayloadAction<number[]>) => {
        state.sportsLoading = false
        if (state.currentUser) {
          state.currentUser.sports = action.payload
        }
      })
      .addCase(updateSportsThunk.rejected, (state, action) => {
        state.sportsLoading = false
        state.error = action.payload as string
      })
      .addCase(updatePreferencesThunk.pending, (state) => {
        state.profileLoading = true
        state.error = null
      })
      .addCase(
        updatePreferencesThunk.fulfilled,
        (
          state,
          action: PayloadAction<{
            interests: string[]
            contentStyles: string[]
            preferredBrands: string[]
            excludedCategories: string[]
          }>,
        ) => {
          state.profileLoading = false
          if (state.currentUser) {
            state.currentUser.preferences = action.payload
          }
        },
      )
      .addCase(updatePreferencesThunk.rejected, (state, action) => {
        state.profileLoading = false
        state.error = action.payload as string
      })
      .addCase(
        updateSchoolThunk.fulfilled,
        (state, action: PayloadAction<{ id: string; name: string } | null>) => {
          if (state.currentUser) {
            state.currentUser.school = action.payload
            state.currentUser.school_id = action.payload ? Number(action.payload.id) : null
          }
        },
      )
      .addCase(updateAvatarThunk.pending, (state) => {
        state.profileLoading = true
        state.error = null
      })
      .addCase(updateAvatarThunk.fulfilled, (state, action: PayloadAction<string>) => {
        state.profileLoading = false
        if (state.currentUser) {
          state.currentUser.avatar = action.payload
        }
      })
      .addCase(updateAvatarThunk.rejected, (state, action) => {
        state.profileLoading = false
        state.error = action.payload as string
      })
      .addCase(requestMagicLinkThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(requestMagicLinkThunk.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(requestMagicLinkThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(verifyMagicLinkThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyMagicLinkThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false
        state.currentUser = action.payload
      })
      .addCase(verifyMagicLinkThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(verifyPhoneCodeThunk.fulfilled, (state, action) => {
        if (state.currentUser) {
          state.currentUser.phone_verified = true
          state.currentUser.phone_verification_required = false
          state.currentUser.phone = action.meta.arg.phone
        }
      })
      .addCase(removePhoneThunk.fulfilled, (state) => {
        if (state.currentUser) {
          state.currentUser.phone = null
          state.currentUser.phone_verified = false
        }
      })
  },
})

// Export actions for logout and updating user data
export const { updateUser, clearUser } = userSlice.actions

export default userSlice.reducer
